import {
  AvatarFallback,
  AvatarImage,
  AvatarInitials,
  BadgedAvatar,
  type BadgedAvatarRootProps,
  BaseAvatar,
  Icon,
  cn,
} from '@glade-ai/ui';
import { type CSSProperties, type ComponentPropsWithRef, forwardRef, useMemo } from 'react';

import nameToInitials from '@/helpers/nameToInitials';
import { useUserProfile } from '@/providers/Auth';
import { type Asset } from '@/typings/api-models';

type UserAvatarProps = ComponentPropsWithRef<'div'> &
  Pick<BadgedAvatarRootProps, 'size' | 'badgeScale'> & {
    // TODO(will): ‘url’ isn’t the best term, but changing it seems to risk needing an ontological change.
    url?: string | null | Pick<Asset, 'url'>;
    name?: string | null;
    color?: string | null;
    isActive?: boolean;
  };

/** Checks if a user name indicates an anonymous or placeholder user. */
const userIsAnonymous = (name?: string | null): boolean => name === 'Anonymous' || name === 'Contact' || name === 'You';

/** Computes background and text colors for user avatar fallbacks. */
const useUserImageColor = ({
  isAnonymous,
  color,
  url,
}: Pick<UserAvatarProps, 'url' | 'color'> & { isAnonymous?: boolean }): CSSProperties =>
  useMemo(
    () => ({
      backgroundColor:
        (isAnonymous && 'var(--color-gray-25)') ||
        color ||
        (typeof url === 'string' && url.startsWith('#') && url) ||
        (!url && 'var(--color-gray-25)') ||
        'transparent',
      color:
        (isAnonymous && 'var(--color-gray-75)') ||
        ((color || (typeof url === 'string' && url.startsWith('#'))) && 'var(--color-gray-0)') ||
        'var(--color-gray-75)',
    }),
    [color, isAnonymous, url],
  );

/** Displays a user avatar with image, initials fallback, and optional badge. */
const UserAvatar = forwardRef<HTMLDivElement, UserAvatarProps>(
  ({ url, name, size = 8, color, children, className, ...props }, forwardedRef) => {
    const isAnonymous = userIsAnonymous(name);
    const colors = useUserImageColor({ color, isAnonymous, url });

    const imageUrl = typeof url === 'string' ? url : url?.url;
    const urlIsString = typeof imageUrl === 'string';
    const isColorUrl = urlIsString && imageUrl.startsWith('#');

    const initials = nameToInitials(isAnonymous ? '' : name);

    return (
      <BadgedAvatar {...props} className={cn('rounded-full', className)} size={size} ref={forwardedRef}>
        <BaseAvatar>
          <AvatarFallback style={colors}>
            <span className='sr-only'>{name}</span>
            {initials ? <AvatarInitials initials={initials} /> : <Icon icon='ph--user--fill' size='full' />}
          </AvatarFallback>
          {imageUrl && !isColorUrl && <AvatarImage src={imageUrl} alt={name || ''} />}
        </BaseAvatar>
        {children}
      </BadgedAvatar>
    );
  },
);

UserAvatar.displayName = 'UserAvatar';

type ProfileAvatarProps = Omit<UserAvatarProps, 'url' | 'name' | 'color'>;

/** Displays the current user's avatar using their profile data. */
const ProfileAvatar = forwardRef<HTMLDivElement, ProfileAvatarProps>(({ ...props }, forwardedRef) => {
  const profile = useUserProfile();

  return (
    <UserAvatar
      url={profile?.image?.url}
      name={profile?.name}
      color={profile?.primaryColour?.hex}
      {...props}
      ref={forwardedRef}
    />
  );
});

ProfileAvatar.displayName = 'ProfileAvatar';

export { UserAvatar, ProfileAvatar, userIsAnonymous, useUserImageColor };
export type { ProfileAvatarProps, UserAvatarProps };
